import React, { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LoginContext } from '../../../context/LoginContext';

const DEFAULT_MESSAGE =
  'Your profile is under approval. Please wait up to 24 hours — we will notify you by SMS and email.';

const AccountPendingApproval = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useContext(LoginContext);
  const message = location.state?.message || DEFAULT_MESSAGE;

  const goToLogin = (e) => {
    e.preventDefault();
    logout();
    navigate('/login', {
      state: {
        message: 'If your account was approved, please log in with your mobile and password.',
      },
    });
  };

  return (
    <div className="hs-pending-page">
      <style>{`
.hs-pending-page {
  width: 100%;
  min-height: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 16px 1.25rem 2.5rem;
  box-sizing: border-box;
  background: transparent;
  clear: both;
  position: relative;
  z-index: 1;
  float: none;
}

.hs-pending-card {
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 16px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.05);
  padding: 2rem 1.75rem 1.75rem;
  text-align: center;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
}

.hs-pending-card * {
  box-sizing: border-box;
  text-transform: none !important;
}

.hs-pending-icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 1.1rem;
  border-radius: 50%;
  background: #fff5f7;
  color: #E53D5C;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1;
}

.hs-pending-card h1 {
  margin: 0 0 0.65rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: #111;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.hs-pending-card .hs-pending-msg {
  margin: 0 0 1.5rem;
  font-size: 0.92rem;
  color: #666;
  line-height: 1.55;
}

.hs-pending-steps {
  list-style: none;
  margin: 0 0 1.6rem;
  padding: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.hs-pending-steps li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.hs-pending-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-top: 0.45rem;
  border-radius: 50%;
  background: #E53D5C;
}

.hs-pending-steps li.is-done .hs-pending-dot {
  background: #22a06b;
}

.hs-pending-steps strong {
  display: block;
  font-size: 0.88rem;
  font-weight: 600;
  color: #222;
  line-height: 1.35;
  margin-bottom: 0.15rem;
}

.hs-pending-steps span {
  display: block;
  font-size: 0.8rem;
  color: #888;
  line-height: 1.4;
}

.hs-pending-actions {
  display: flex;
  gap: 0.65rem;
  margin-bottom: 1.1rem;
}

.hs-pending-actions a {
  flex: 1;
  text-align: center;
  text-decoration: none;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 0.7rem 0.75rem;
  border-radius: 10px;
  line-height: 1.2;
}

.hs-pending-home {
  background: #fff;
  color: #444;
  border: 1px solid #e5e5e5;
}

.hs-pending-home:hover {
  background: #fafafa;
}

.hs-pending-support {
  background: #E53D5C;
  color: #fff !important;
  border: 1px solid #E53D5C;
}

.hs-pending-support:hover {
  background: #d43552;
}

.hs-pending-contact {
  margin: 0;
  font-size: 0.78rem;
  color: #999;
  line-height: 1.5;
}

.hs-pending-contact a {
  color: #E53D5C;
  text-decoration: none;
  font-weight: 600;
}

.hs-pending-contact a:hover {
  text-decoration: underline;
}

@media (max-width: 480px) {
  .hs-pending-page {
    padding: 8px 1rem 2rem;
  }

  .hs-pending-card {
    padding: 1.65rem 1.25rem 1.4rem;
  }

  .hs-pending-card h1 {
    font-size: 1.2rem;
  }
}
`}</style>

      <article className="hs-pending-card" aria-labelledby="pending-title">
        <div className="hs-pending-icon" aria-hidden="true">!</div>
        <h1 id="pending-title">Profile under review</h1>
        <p className="hs-pending-msg">{message}</p>

        <ul className="hs-pending-steps">
          <li className="is-done">
            <span className="hs-pending-dot" aria-hidden="true" />
            <div>
              <strong>Registration received</strong>
              <span>Your details are saved securely.</span>
            </div>
          </li>
          <li>
            <span className="hs-pending-dot" aria-hidden="true" />
            <div>
              <strong>Admin review</strong>
              <span>Usually within 24 hours on working days.</span>
            </div>
          </li>
          <li>
            <span className="hs-pending-dot" aria-hidden="true" />
            <div>
              <strong>We’ll notify you</strong>
              <span>SMS and email when your profile is approved.</span>
            </div>
          </li>
        </ul>

        <div className="hs-pending-actions">
          <Link to="/" className="hs-pending-home">Home</Link>
          <a href="/login" className="hs-pending-support" onClick={goToLogin}>Login</a>
        </div>

        <p className="hs-pending-contact">
          Need help? <a href="/contact-us">Contact Support</a>
          {' · '}
          <a href="mailto:support@hindusanjog.com">support@hindusanjog.com</a>
        </p>
      </article>
    </div>
  );
};

export default AccountPendingApproval;
